<template>
  <dialog
    v-if="isShown"
    ref="modalEl"
    :aria-labelledby="$slots.title ? `${componentID}__title` : ''"
    :class="uiModalContentClasses"
    class="ui-modal"
    data-test="ui-modal"
  >
    <div
      data-test="ui-modal__content"
      class="ui-modal__content"
    >
      <div
        :class="{
          'ui-modal__header': true,
          'ui-modal__header--padded': !hasCloseButton,
          'ui-modal__header--hidden-title': hideTitle,
          'sr-only': !showHeader,
        }"
      >
        <h2
          v-if="$slots.title"
          :id="`${componentID}__title`"
          class="ui-modal__title"
          :class="{
            'sr-only': hideTitle,
          }"
        >
          <slot name="title" />
        </h2>

        <button
          v-if="hasCloseButton"
          class="ui-button ui-button--borderless ui-modal__close-button"
          data-test="ui-modal__close-button"
          type="button"
          @click="$emit('close')"
        >
          <span class="sr-only">{{ t('Close') }}</span>

          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
              fill="#112857"
            />
          </svg>
        </button>
      </div>

      <div
        :class="{
          'ui-modal__body': true,
          'ui-modal__body--no-header': !showHeader,
        }"
      >
        <slot />
      </div>
    </div>

    <section
      aria-labelledby="toast-container-dialog-heading"
      aria-live="polite"
      :class="TOAST_CONTAINER_CLASS_NAME"
    >
      <h1
        id="toast-container-dialog-heading"
        class="sr-only"
      >
        {{ t('Messages') }}
      </h1>
    </section>
  </dialog>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onUpdated,
  ref,
  toRefs,
  watch,
  onBeforeMount,
  onBeforeUpdate,
  useSlots,
  onMounted,
} from 'vue'
import { useI18n } from 'vue-i18n'
import dialogPolyfill from 'dialog-polyfill'

import { TOAST_CONTAINER_CLASS_NAME } from '../constants'

const { t } = useI18n()

const props = withDefaults(defineProps<{
  disablePageScroll?: boolean
  escEmitsCloseEvent?: boolean
  hasCloseButton?: boolean
  isOpen: boolean
  noPadding?: boolean
  outsideClickEmitsCloseEvent?: boolean
  size?: 'full-screen' | 'medium' | 'small'
  hideTitle?: boolean
}>(), {
  disablePageScroll: true,
  escEmitsCloseEvent: true,
  hasCloseButton: true,
  hideTitle: false,
  noPadding: false,
  outsideClickEmitsCloseEvent: true,
  size: 'medium',
})

const emit = defineEmits<{
  close: []
  open: []
}>()

const { isOpen } = toRefs(props)

const modalEl = ref<HTMLDialogElement>()

const isShown = ref(isOpen.value)

const uiModalContentClasses = computed(() => {
  return {
    [`ui-modal--${props.size}`]: true,
    ['ui-modal--no-padding']: props.noPadding,
    ['ui-modal--polyfilled']: dialogUnsupported.value,
  }
})

const componentID = computed<string>(() => {
  const randomNumber = Math.round(Math.random() * 10000000)
  return `ui-modal-${randomNumber.toString(36)}`
})

const showHeader = computed<boolean>(() => {
  return !props.hideTitle || props.hasCloseButton
})

// Polyfilling HTML dialog
const dialogUnsupported = computed<boolean>(() => {
  return typeof HTMLDialogElement !== 'function'
})

function registerModal(): void {
  if (dialogUnsupported.value && modalEl.value) {
    dialogPolyfill.registerDialog(modalEl.value)
  }
}

watch(
  isOpen,
  async (value: boolean) => {
    if (value) {
      emit('open')
      if (props.disablePageScroll) {
        document.body.classList.add('ui-modal--open')
      }
      isShown.value = true

      if (!modalEl.value) {
        await nextTick()
      }
      registerModal()
      modalEl.value?.showModal()
    } else {
      if (modalEl.value) {
        if (props.disablePageScroll) {
          document.body.classList.remove('ui-modal--open')
        }
        if (!dialogUnsupported.value) {
          modalEl.value.classList.add('ui-modal--close')
          modalEl.value.addEventListener('animationend', closeModal, { once: true })
        } else {
          emit('close')
          registerModal()
          modalEl.value.close()
          isShown.value = false
        }
      }
    }
  },
  { immediate: true },
)

onUpdated(() => {
  addCloseEventListeners()
})

onMounted(() => {
  addCloseEventListeners()
})

onBeforeUnmount(() => {
  if (props.disablePageScroll) {
    document.body.classList.remove('ui-modal--open')
  }

  if (modalEl.value) {
    const dialog = modalEl.value

    if (props.outsideClickEmitsCloseEvent) {
      dialog.removeEventListener('click', outsideClickHandler)
      dialog.removeEventListener('touchstart', outsideClickHandler)
    }

    dialog.removeEventListener('cancel', escHandler)
    dialog.removeEventListener('animationend', closeModal)
  }
})

function addCloseEventListeners() {
  if (!modalEl.value || !isOpen.value) {
    return
  }

  if (props.outsideClickEmitsCloseEvent) {
    modalEl.value.addEventListener('click', outsideClickHandler)
    modalEl.value.addEventListener('touchstart', outsideClickHandler)
  }

  modalEl.value.addEventListener('cancel', escHandler)
}

function outsideClickHandler(event: Event) {
  if ((event.target as HTMLElement).nodeName === 'DIALOG') {
    event.stopPropagation()
    emit('close')
  }
}

function escHandler(event: Event): void {
  event.preventDefault()
  if (props.escEmitsCloseEvent) {
    emit('close')
  }
}

function closeModal(): void {
  const dialog = modalEl.value
  dialog?.classList.remove('ui-modal--close')
  emit('close')
  dialog?.close()
  isShown.value = false
}

// Check if #title slot exists
const slots = useSlots()

function checkValidity(): void {
  if (!slots.title && !props.hideTitle) {
    throw Error('UiModal: #title slot is required, set hide-title prop to true to hide it.')
  }
}

onBeforeMount(() => checkValidity())
onBeforeUpdate(() => checkValidity())
</script>

<style lang="scss">
.ui-modal::backdrop {
  content: '';
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 1040;
  background-color: rgba(0, 0, 0, 0.5);
}

.ui-modal {
  background-color: $sc-white;
  color: inherit;
  border-radius: $sc-border-radius;
  border: 1px solid $sc-extra-dark-blue;
  margin: auto;
  margin-bottom: 0;
  z-index: 1041;
  max-width: calc(100% - ($sc-spacing-8 * 2));
  max-height: calc(100% - ($sc-spacing-8 * 2));
  overflow: auto;
  word-break: break-word;
  padding: 0;

  @include breakpoint-min-width(small) {
    margin-bottom: auto;
  }
  @include breakpoint-min-width(medium) {
    max-width: calc(100% - ($sc-spacing-16 * 2));
    max-height: calc(100% - ($sc-spacing-16 * 2));
  }
}

.ui-modal--open {
  overflow: hidden !important;
}

.ui-modal--small {
  width: 450px;
}

.ui-modal--medium {
  width: 650px;
}

.ui-modal--full-screen {
  height: 100%;
  width: 100%;
}

.ui-modal--no-padding > .ui-modal__content > .ui-modal__header,
.ui-modal--no-padding > .ui-modal__content > .ui-modal__body,
.ui-modal--no-padding > .ui-modal__content > .ui-modal__header > .ui-modal__title {
  padding: 0;
}

.ui-modal__header {
  border-bottom: none;
  align-items: center;
  display: flex;
  align-items: flex-start;
  padding: 0 0 0 $sc-spacing-20;
  margin-bottom: $sc-spacing-16;

  &--hidden-title {
    justify-content: flex-end;
  }
}
.ui-modal__header--padded {
  padding-right: $sc-spacing-20;
}

.ui-modal__body {
  padding: 0 $sc-spacing-20 $sc-spacing-20 $sc-spacing-20;
  &--no-header {
    padding-top: $sc-spacing-20;
  }
}

.ui-modal__title {
  font-weight: 600;
  font-size: 32px;
  margin: 0;
  color: $sc-dark-blue;

  padding-top: $sc-spacing-20;
  flex-grow: 1;

  @include breakpoint-max-width(medium) {
    font-size: 25px;
  }
}

.ui-modal__icon-wrapper {
  margin-right: $sc-spacing-16;
  line-height: 40px;
  flex-grow: 0;
  padding-top: $sc-spacing-20;
  & * {
    vertical-align: middle;
  }
  @include breakpoint-max-width(medium) {
    margin-right: $sc-spacing-8;
    line-height: 30px;
  }
}

.ui-modal__close-button {
  background-color: transparent;
  border: none;
  cursor: pointer;
  line-height: 1;
  text-overflow: initial;
  flex-shrink: 0;
  margin-right: $sc-spacing-4;
  margin-top: $sc-spacing-4;

  @include breakpoint-min-width(medium) {
    margin-right: $sc-spacing-4;
    margin-top: $sc-spacing-4;
  }

  &:hover {
    background-color: $sc-informative-10;
  }

  &:active {
    background-color: $sc-informative-20;
  }
}

.ui-modal--close,
.ui-modal--close::backdrop {
  animation: modal-fade 0.3s ease forwards;
}

.ui-modal--close::backdrop {
  animation-name: backdrop-fade;
}

@keyframes modal-fade {
  to {
    opacity: 0;
  }
}

@keyframes backdrop-fade {
  to {
    background: transparent;
  }
}

.ui-modal--polyfilled {
  &.ui-modal,
  &.ui-modal + .backdrop,
  & ._dialog_overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  &.ui-modal {
    width: -moz-fit-content;
    width: -webkit-fit-content;
    width: fit-content;
    height: -moz-fit-content;
    height: -webkit-fit-content;
    height: fit-content;
    display: block;
  }

  &.ui-modal:not([open]) {
    display: none;
  }

  &.ui-modal + .backdrop {
    background: rgba(0, 0, 0, 0.5);
  }

  &.ui-modal.fixed {
    position: fixed;
    top: 50%;
    transform: translate(0, -50%);
  }

  &.ui-modal--close + .backdrop {
    animation: backdrop-fade 0.3s ease forwards;

    @media (prefers-reduced-motion) {
      animation-name: none;
    }
  }
}

.ui-modal-toast-container {
  inset: 0;
  position: fixed;
  pointer-events: none;
}
</style>
